import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Loader2, DollarSign, FileText, CreditCard, AlertTriangle, TrendingUp, Receipt, Settings, FileBarChart2, Wallet } from "lucide-react";
import { getDashboardMetrics } from "@/lib/accounting/accounting.functions";
import { fmtMoney } from "@/lib/accounting/exports";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export const Route = createFileRoute("/_authenticated/admin/accounting")({
  head: () => ({ meta: [{ title: "Accounting — Admin" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: AccountingLayout,
});

const SUB_NAV = [
  { to: "/admin/accounting", label: "Dashboard", exact: true, icon: TrendingUp },
  { to: "/admin/accounting/invoices", label: "Invoices", icon: FileText },
  { to: "/admin/accounting/payments", label: "Payments", icon: CreditCard },
  { to: "/admin/accounting/deposits", label: "Deposits", icon: Wallet },
  { to: "/admin/accounting/receivables", label: "A/R Aging", icon: AlertTriangle },
  { to: "/admin/accounting/profitability", label: "Profitability", icon: DollarSign },
  { to: "/admin/accounting/expenses", label: "Expenses", icon: Receipt },
  { to: "/admin/accounting/reports", label: "Reports", icon: FileBarChart2 },
  { to: "/admin/accounting/settings", label: "Settings", icon: Settings },
];

function AccountingLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isRoot = pathname === "/admin/accounting" || pathname === "/admin/accounting/";
  return (
    <div className="space-y-6">
      <header>
        <p className="text-amber-300 text-[10px] tracking-[0.25em] uppercase font-semibold">Bookkeeping</p>
        <h1 className="font-serif text-3xl text-white mt-1">Accounting</h1>
        <p className="text-sm text-zinc-500 mt-1">Invoices, payments, A/R, expenses, and profitability — built for export.</p>
      </header>
      <nav className="flex flex-wrap gap-1 border-b border-white/10 pb-3">
        {SUB_NAV.map((n) => {
          const active = n.exact ? pathname === n.to : pathname.startsWith(n.to) && n.to !== "/admin/accounting";
          const Icon = n.icon;
          return (
            <Link
              key={n.to}
              to={n.to}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                active
                  ? "bg-amber-300/10 border-amber-300/30 text-amber-200"
                  : "border-transparent text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
            >
              <Icon className="size-3.5" /> {n.label}
            </Link>
          );
        })}
      </nav>
      {isRoot ? <Dashboard /> : <Outlet />}
    </div>
  );
}

const RANGES = [
  { id: "ytd", label: "YTD" },
  { id: "month", label: "This month" },
  { id: "lastmonth", label: "Last month" },
  { id: "week", label: "This week" },
  { id: "today", label: "Today" },
] as const;

function rangeOf(id: string): { start: string; end: string } {
  const now = new Date();
  const end = now.toISOString();
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
  if (id === "today") return { start: startOf(now), end };
  if (id === "week") {
    const w = new Date(now); w.setDate(now.getDate() - now.getDay()); return { start: startOf(w), end };
  }
  if (id === "month") return { start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), end };
  if (id === "lastmonth") {
    const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const e = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    return { start: s.toISOString(), end: e.toISOString() };
  }
  return { start: new Date(now.getFullYear(), 0, 1).toISOString(), end };
}

function Dashboard() {
  const fn = useServerFn(getDashboardMetrics);
  const [rangeId, setRangeId] = useState<string>("ytd");
  const range = useMemo(() => rangeOf(rangeId), [rangeId]);
  const { data, isLoading } = useQuery({
    queryKey: ["accounting", "dashboard", rangeId],
    queryFn: () => fn({ data: range }),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        {RANGES.map((r) => (
          <button
            key={r.id}
            onClick={() => setRangeId(r.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
              rangeId === r.id ? "bg-white text-zinc-950 border-white" : "border-white/10 text-zinc-400 hover:text-white"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {isLoading || !data ? (
        <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            <Stat label="Total revenue" value={fmtMoney(data.totalRevenue)} tone="positive" />
            <Stat label="Outstanding" value={fmtMoney(data.outstanding)} tone="accent" />
            <Stat label={`Overdue (${data.overdue.count})`} value={fmtMoney(data.overdue.amount)} tone="danger" />
            <Link to="/admin/accounting/deposits" className="contents">
              <Stat label="Deposits collected" value={fmtMoney(data.deposits)} />
            </Link>
            <Stat label="Final payments" value={fmtMoney(data.finals)} />
            <Stat label="Refunds issued" value={fmtMoney(data.refundsIssued)} />
            <Stat label="Sales tax collected" value={fmtMoney(data.tax)} />
            <Stat label="Avg invoice" value={fmtMoney(data.avgInvoice)} />
            <Stat label="Avg days to payment" value={`${data.avgDaysToPay.toFixed(1)} days`} />
            <Stat label="Jobs paid in full" value={String(data.paidInFull)} tone="positive" />
            <Stat label="Jobs with balance due" value={String(data.balanceDue)} tone="accent" />
            <Stat label="Invoices in range" value={String(data.invoiceCount)} />
          </div>

          <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <h2 className="text-sm text-zinc-300 font-medium mb-4">Revenue by month</h2>
            <div className="h-64">
              <ResponsiveContainer>
                <LineChart data={data.revenueByMonth}>
                  <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="month" stroke="#a1a1aa" fontSize={11} />
                  <YAxis stroke="#a1a1aa" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} formatter={(v: number) => fmtMoney(v)} />
                  <Line type="monotone" dataKey="amount" stroke="#fbbf24" strokeWidth={2} dot={{ fill: "#fbbf24" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          <div className="flex flex-wrap gap-2">
            <Link to="/admin/accounting/invoices" className="text-xs px-3 py-2 rounded-full bg-white text-zinc-950 font-semibold">New invoice →</Link>
            <Link to="/admin/accounting/receivables" className="text-xs px-3 py-2 rounded-full border border-white/10 text-zinc-200">Review A/R</Link>
            <Link to="/admin/accounting/reports" className="text-xs px-3 py-2 rounded-full border border-white/10 text-zinc-200">Export reports</Link>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "positive" | "accent" | "danger" }) {
  const c = tone === "positive" ? "text-emerald-300" : tone === "danger" ? "text-red-300" : tone === "accent" ? "text-amber-300" : "text-white";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</p>
      <p className={`mt-2 font-serif text-2xl ${c}`}>{value}</p>
    </div>
  );
}